import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppState } from '../../state';
import { Button, CircularProgress, Grid, makeStyles } from '@material-ui/core';
import { Theme } from '@material-ui/core';
import IntroContainer from '../IntroContainer/IntroContainer';
import Typography from '@material-ui/core/Typography';
import { useLocation, useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  errorMessage: {
    color: 'red',
    display: 'flex',
    alignItems: 'center',
    margin: '1em 0 0.2em',
    '& svg': {
      marginRight: '0.4em',
    },
  },
  gutterBottom: {
    marginBottom: '1em',
  },
  passcodeContainer: {
    minHeight: '120px',
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  fileButtonLoadingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginLeft: '40px',
  },
}));

export default function LoginPage() {
  const classes = useStyles();
  const { signIn, user, isAuthReady } = useAppState();
  const history = useHistory();
  const location = useLocation<{ from: Location }>();
  const [authError, setAuthError] = useState<Error | null>(null);

  const isAuthEnabled = Boolean(process.env.REACT_APP_SET_AUTH);

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // A successful sign-in navigates away and unmounts this page, so state is only updated while it is still mounted.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const login = () => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    setAuthError(null);
    signIn?.('8fWru2wcwxck')
      .then(() => {
        history.replace(location?.state?.from || { pathname: '/' });
      })
      .catch(err => {
        if (isMounted.current) setAuthError(err);
      })
      .finally(() => {
        if (isMounted.current) setIsLoggingIn(false);
      });
  };

  // const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
  //   e.preventDefault();
  //   login();
  // };

  useEffect(() => {
    if (!isAuthReady) return;
    const searchStr = window.sessionStorage.getItem('searchStr');
    if (!searchStr) {
      window.sessionStorage.setItem('searchStr', location?.state?.from.search);
    }
    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady]);

  // Redirect from a layout effect instead of during render (which React warns about). It still runs synchronously
  // in the same commit, i.e. before the sign-in promise above redirects to the originally requested location.
  const shouldRedirectHome = Boolean(user) || !isAuthEnabled;
  useLayoutEffect(() => {
    if (shouldRedirectHome) history.replace('/');
  }, [shouldRedirectHome, history]);

  if (!isAuthReady) {
    return null;
  }

  return (
    <IntroContainer>
      {process.env.REACT_APP_SET_AUTH === 'passcode' && (
        <>
          {authError ? (
            <>
              <Typography variant="h5" className={classes.gutterBottom}>
                Unable to connect
              </Typography>
              <Typography variant="body1" className={classes.errorMessage}>
                {authError.message}
              </Typography>
              <Button variant="contained" color="primary" className={classes.submitButton} onClick={login}>
                Retry
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" className={classes.gutterBottom}>
                Please wait...
              </Typography>
              <Grid container justifyContent="flex-end">
                <CircularProgress size={36} className={classes.fileButtonLoadingSpinner} />
              </Grid>
            </>
          )}
        </>
      )}
    </IntroContainer>
  );
}
